import {
  defaultMatterTeamFormValues,
  MAX_MATTER_TEAM_LABEL_LENGTH,
  matterTeamFormSchema,
  type MatterTeamFormValues,
} from "@/components/features/settings/defaults/matter-teams/matterTeamFormSchema";
import { MatterTeammatesInlineFields } from "@/components/features/settings/defaults/matter-teams/MatterTeammatesInlineFields";
import {
  ActionConfirmationModal,
  Button,
  Form,
  FormSubmitButton,
  LoadingSpinner,
  TextAreaFormField,
  TextFormField,
  Typography,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import {
  useCreateMatterTeam,
  useDeleteMatterTeam,
  useUpdateMatterTeam,
} from "@/lib/data/react-query/matters/matter-team";
import { retrieveMatterTeammatesQueryOptions } from "@/lib/data/react-query/matters/matter-teammate";
import {
  createMatterTeammateV1,
  deleteMatterTeammateV1,
} from "@/lib/data/requests/matters/matter-teammate";

import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { queryKeys } from "@/lib/data/static";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type MatterTeam } from "@/lib/types";
import { formatUsersName } from "@/lib/utils/format";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toLower } from "lodash";
import { Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";

interface AddOrEditMatterTeamModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

type ConditionalProps =
  | {
      isReadOnly?: boolean;
      editing: true;
      activeMatterTeam: Pick<MatterTeam, "id" | "dateModified"> &
        Partial<MatterTeam>;
      setActiveMatterTeamId: (id: string) => void;
    }
  | {
      isReadOnly?: never;
      editing: false;
      activeMatterTeam?: never;
      setActiveMatterTeamId?: never;
    };

const defaultFormValues = defaultMatterTeamFormValues;

const FORM_ID = "matter-team-form";

type DisplayedTeammate = {
  matterTeammateId: string;
  label: string;
  firstName?: string;
  lastName?: string;
  id: string;
};

const getTeammateDisplayName = (teammate: DisplayedTeammate) =>
  formatUsersName({
    firstName: teammate.firstName ?? "",
    lastName: teammate.lastName ?? "",
  }) ?? "";

export function AddOrEditMatterTeamModal({
  open,
  setOpen,
  activeMatterTeam,
  editing,
  isReadOnly,
}: AddOrEditMatterTeamModalProps & ConditionalProps) {
  const { hasActionPermission } = useUser();
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSavingTeammates, setIsSavingTeammates] = useState(false);
  const { data: teammatesData, isLoading: isTeammatesLoading } = useQuery(
    retrieveMatterTeammatesQueryOptions({
      id: activeMatterTeam?.id ?? "",
    }),
  );
  const teammates = useMemo(
    () => teammatesData?.data ?? [],
    [teammatesData?.data],
  );

  const form = useForm<MatterTeamFormValues>({
    resolver: zodResolver(matterTeamFormSchema),
    defaultValues: defaultFormValues,
    mode: "onSubmit",
  });

  const deletedMatterTeammateIds = form.watch("deletedMatterTeammateIds");

  const { data: matterSettingsData } = useQuery(
    retrieveMatterSettingsQueryOptions(),
  );
  const matterTeams = matterSettingsData?.data.teams ?? [];
  const matterRoles = useMemo(
    () => matterSettingsData?.data.roles ?? [],
    [matterSettingsData?.data.roles],
  );
  const createMatterTeam = useCreateMatterTeam();
  const editMatterTeam = useUpdateMatterTeam();
  const deleteMatterTeam = useDeleteMatterTeam();
  const isFormLoading =
    createMatterTeam.isPending ||
    editMatterTeam.isPending ||
    deleteMatterTeam.isPending ||
    isSavingTeammates;

  const visibleServerTeammates = useMemo<DisplayedTeammate[]>(
    () =>
      teammates
        .filter((teammate) => !deletedMatterTeammateIds.includes(teammate.id))
        .map((teammate) => ({
          matterTeammateId: teammate.id,
          id: teammate.id,
          firstName: teammate.firstName,
          lastName: teammate.lastName,
          label: teammate.roleLabel,
        })),
    [deletedMatterTeammateIds, teammates],
  );

  const saveTeammateChanges = async (
    id: string,
    {
      newMatterTeammates,
      deletedMatterTeammateIds,
    }: Pick<
      MatterTeamFormValues,
      "newMatterTeammates" | "deletedMatterTeammateIds"
    >,
  ) => {
    const validNewTeammates = newMatterTeammates.filter(
      (draft) => draft.id && draft.id,
    );

    if (
      validNewTeammates.length === 0 &&
      deletedMatterTeammateIds.length === 0
    ) {
      return;
    }

    setIsSavingTeammates(true);
    try {
      await Promise.all([
        ...validNewTeammates.map((draft) => {
          return createMatterTeammateV1({
            roleId: draft.matterRoleId,
            teamId: id,
            user: { userId: draft.id },
          });
        }),
        ...deletedMatterTeammateIds.map((teammateId) =>
          deleteMatterTeammateV1({ id: teammateId, teamId: id }),
        ),
      ]);
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matterTeammates, id],
      });
    } finally {
      setIsSavingTeammates(false);
    }
  };

  const onSubmit = async (data: MatterTeamFormValues) => {
    if (
      matterTeams.some(
        (matterTeam) =>
          toLower(matterTeam.label.trim()) === toLower(data.label.trim()) &&
          matterTeam.id !== activeMatterTeam?.id,
      )
    ) {
      form.setError("label", {
        message: "Matter team label must be unique",
      });
      return;
    }

    const pendingTeammateChanges = {
      newMatterTeammates: form.getValues("newMatterTeammates"),
      deletedMatterTeammateIds: form.getValues("deletedMatterTeammateIds"),
    };
    const teamData = {
      label: data.label,
      description: data.description,
    };

    if (editing) {
      await editMatterTeam.mutateAsync({
        id: activeMatterTeam.id,
        dateModified: activeMatterTeam.dateModified,
        ...teamData,
      });
      await saveTeammateChanges(activeMatterTeam.id, pendingTeammateChanges);
    } else {
      const createdTeam = await createMatterTeam.mutateAsync({
        ...teamData,
      });
      await saveTeammateChanges(createdTeam.data.id, pendingTeammateChanges);
    }

    setOpen(false);
    form.reset();
  };

  const handleDeleteTeammate = (teammate: { matterTeammateId: string }) => {
    form.setValue(
      "deletedMatterTeammateIds",
      [...deletedMatterTeammateIds, teammate.matterTeammateId],
      { shouldDirty: true },
    );
  };

  const handleDelete = async () => {
    setOpen(false);
    if (editing) {
      await deleteMatterTeam
        .mutateAsync({
          id: activeMatterTeam.id,
        })
        .then(() => {
          setIsDeleting(false);
        });
    }
  };

  const sheetTitle = useMemo(
    () =>
      isReadOnly
        ? "View matter team"
        : editing
          ? "Update matter team"
          : "Create matter team",
    [isReadOnly, editing],
  );

  const sheetDescription = useMemo(
    () =>
      isReadOnly
        ? "View the matter team details below"
        : editing
          ? "Update the matter team details below"
          : "Create a new matter team to your firm",
    [isReadOnly, editing],
  );

  const activeMatterTeamId = activeMatterTeam?.id;
  const wasOpenRef = useRef(false);

  useEffect(() => {
    const isOpening = open && !wasOpenRef.current;
    wasOpenRef.current = open;

    if (!isOpening) return;

    form.reset(
      editing
        ? {
            ...defaultFormValues,
            label: activeMatterTeam.label,
            description: activeMatterTeam.description ?? "",
          }
        : defaultFormValues,
    );
  }, [open, editing, activeMatterTeam, activeMatterTeamId, form]);

  const canDelete =
    !isReadOnly &&
    editing &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.matterTeam,
      ACTION_PERMISSION_VALUES.delete,
    );

  const deleteButton = canDelete ? (
    <div>
      <Button
        type="button"
        variant="destructive"
        onClick={() => {
          setIsDeleting(true);
        }}
      >
        Delete
      </Button>
    </div>
  ) : null;

  return (
    <>
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={sheetTitle}
          description={sheetDescription}
          isDirty={form.formState.isDirty}
          loading={isFormLoading}
          size="2xl"
          footer={
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              {deleteButton}
              <div className="flex grow justify-end space-x-2">
                <FormCancelButton
                  label={isReadOnly ? "Close" : "Cancel"}
                  setOpen={setOpen}
                  isDirty={form.formState.isDirty}
                />
                {!isReadOnly && <FormSubmitButton submitFormId={FORM_ID} />}
              </div>
            </div>
          }
        >
          <form
            id={FORM_ID}
            className="flex min-h-0 grow flex-col overflow-hidden"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <div className="overflow-y-auto">
              <fieldset
                disabled={isReadOnly}
                className={slideoverFieldsetClassName}
              >
                <TextFormField
                  register={form.register("label")}
                  label="Label*"
                  maxLength={MAX_MATTER_TEAM_LABEL_LENGTH}
                />
                <div className="col-span-2">
                  <TextAreaFormField
                    register={form.register("description")}
                    label="Description"
                  />
                </div>
                {!isReadOnly && (
                  <>
                    <Typography
                      variant="heading"
                      className="col-span-full mt-4 mb-2"
                    >
                      Team members
                    </Typography>
                    {editing && isTeammatesLoading && <LoadingSpinner />}

                    {visibleServerTeammates.map((teammate) => (
                      <div
                        key={teammate.id}
                        className="col-span-full flex flex-col"
                      >
                        <div className="flex flex-col">
                          <Typography variant="subHeading">
                            {teammate.label}
                          </Typography>
                        </div>
                        <div className="flex items-center">
                          <Typography>
                            {getTeammateDisplayName(teammate)}
                          </Typography>
                          <Button
                            type="button"
                            className="text-destructive ml-2 max-h-6 max-w-6"
                            variant="ghost"
                            onClick={() => {
                              handleDeleteTeammate(teammate);
                            }}
                          >
                            <Trash2 />
                          </Button>
                        </div>
                      </div>
                    ))}

                    <MatterTeammatesInlineFields
                      parentForm={form}
                      matterRoles={matterRoles}
                      enabled={open}
                    />
                  </>
                )}
              </fieldset>
            </div>
          </form>
        </AppModal>
      </Form>

      <ActionConfirmationModal
        title={`Are you sure you want to delete this matter team?`}
        open={isDeleting && !!activeMatterTeam}
        setOpen={setIsDeleting}
        afterActionFn={async () => {
          if (activeMatterTeam) {
            await handleDelete();
          }
        }}
      />
    </>
  );
}
